import { useState, useEffect } from 'react';
import { profiloListImportBatches, profiloGetBatchItems } from '@/lib/profiloImport';
import { formatTRY } from '@/lib/currencyFormat';

const MONTH_NAMES = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

interface Props {
  refreshKey: number;
}

export default function ProfiloImportHistory({ refreshKey }: Props) {
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedBatch, setExpandedBatch] = useState<string | null>(null);
  const [batchItems, setBatchItems] = useState<Record<string, any[]>>({});
  const [loadingItems, setLoadingItems] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    profiloListImportBatches().then((result) => {
      if (result.error) {
        setError(result.error);
      } else {
        setBatches(result.batches || []);
      }
      setLoading(false);
    });
  }, [refreshKey]);

  const toggleBatch = async (batchId: string) => {
    if (expandedBatch === batchId) {
      setExpandedBatch(null);
      return;
    }
    setExpandedBatch(batchId);
    if (!batchItems[batchId]) {
      setLoadingItems(batchId);
      const result = await profiloGetBatchItems(batchId);
      setBatchItems((prev) => ({ ...prev, [batchId]: result.items || [] }));
      setLoadingItems(null);
    }
  };

  const periodLabel = (b: any) => {
    if (b.period_month && b.period_year) {
      return `${MONTH_NAMES[b.period_month - 1]} ${b.period_year}`;
    }
    return '—';
  };

  const statusBadge = (status: string) => {
    if (status === 'completed') return 'bg-emerald-100 text-emerald-700';
    if (status === 'processing') return 'bg-amber-100 text-amber-700';
    if (status === 'failed') return 'bg-red-100 text-red-700';
    return 'bg-background-100 text-foreground-500';
  };

  const statusLabel = (status: string) => {
    if (status === 'completed') return 'Tamamlandı';
    if (status === 'processing') return 'İşleniyor';
    if (status === 'failed') return 'Başarısız';
    return status;
  };

  const itemStatusColor = (status: string) => {
    if (status === 'new') return 'text-emerald-600';
    if (status === 'update') return 'text-amber-600';
    if (status === 'unchanged') return 'text-foreground-400';
    if (status === 'error') return 'text-red-500';
    return 'text-foreground-400';
  };

  const itemStatusLabel = (status: string) => {
    if (status === 'new') return 'Yeni';
    if (status === 'update') return 'Güncellendi';
    if (status === 'unchanged') return 'Değişmedi';
    if (status === 'error') return 'Hata';
    return status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-8 h-8 rounded-full border-2 border-primary-100 border-t-primary-500 animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
        <i className="ri-error-warning-line mr-2"></i>{error}
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-10">
        <div className="w-12 h-12 rounded-xl bg-background-100 flex items-center justify-center">
          <i className="ri-file-list-line text-xl text-foreground-300"></i>
        </div>
        <p className="text-sm text-foreground-400">Henüz Profilo fiyat listesi yüklenmedi.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {batches.map((batch) => (
        <div key={batch.id} className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleBatch(batch.id)}
            className="w-full flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors"
          >
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-file-excel-line text-primary-500 text-sm"></i>
              </div>
              <div className="text-left min-w-0">
                <p className="text-sm font-semibold text-foreground-800 truncate max-w-xs">{batch.file_name}</p>
                <p className="text-xs text-foreground-400">{periodLabel(batch)} • {new Date(batch.imported_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-3 text-xs">
                <span className="text-emerald-600">+{batch.new_products}</span>
                <span className="text-amber-600">↑{batch.updated_products}</span>
                <span className="text-foreground-400">={batch.unchanged_products}</span>
                {batch.error_count > 0 && <span className="text-red-500">✕{batch.error_count}</span>}
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusBadge(batch.status)}`}>
                {statusLabel(batch.status)}
              </span>
              <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${expandedBatch === batch.id ? 'rotate-180' : ''}`}></i>
            </div>
          </button>

          {expandedBatch === batch.id && (
            <div className="border-t border-background-200">
              {loadingItems === batch.id ? (
                <div className="flex items-center justify-center py-6">
                  <div className="w-6 h-6 border-2 border-primary-100 border-t-primary-500 rounded-full animate-spin"></div>
                </div>
              ) : (
                <div>
                  {/* Batch summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 p-4 bg-background-100/50">
                    <div className="text-center"><p className="text-base font-bold text-foreground-900">{batch.total_products}</p><p className="text-xs text-foreground-400">Toplam</p></div>
                    <div className="text-center"><p className="text-base font-bold text-emerald-600">{batch.new_products}</p><p className="text-xs text-foreground-400">Yeni</p></div>
                    <div className="text-center"><p className="text-base font-bold text-amber-600">{batch.updated_products}</p><p className="text-xs text-foreground-400">Güncellendi</p></div>
                    <div className="text-center"><p className="text-base font-bold text-foreground-400">{batch.unchanged_products}</p><p className="text-xs text-foreground-400">Değişmedi</p></div>
                    <div className="text-center"><p className={`text-base font-bold ${batch.error_count > 0 ? 'text-red-500' : 'text-foreground-400'}`}>{batch.error_count}</p><p className="text-xs text-foreground-400">Hata</p></div>
                  </div>

                  {/* Items table */}
                  {batchItems[batch.id] && batchItems[batch.id].length > 0 && (
                    <div className="overflow-x-auto max-h-[300px]">
                      <table className="w-full min-w-[600px] text-xs">
                        <thead className="bg-background-100 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-foreground-400 font-medium">Durum</th>
                            <th className="px-3 py-2 text-left text-foreground-400 font-medium">Model Kodu</th>
                            <th className="px-3 py-2 text-left text-foreground-400 font-medium">Kategori</th>
                            <th className="px-3 py-2 text-right text-foreground-400 font-medium">Eski Maliyet</th>
                            <th className="px-3 py-2 text-right text-foreground-400 font-medium">Yeni Maliyet</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchItems[batch.id].slice(0, 100).map((item) => (
                            <tr key={item.id} className="border-b border-background-100">
                              <td className="px-3 py-2">
                                <span className={`font-medium ${itemStatusColor(item.status)}`}>{itemStatusLabel(item.status)}</span>
                              </td>
                              <td className="px-3 py-2 font-mono text-foreground-700">{item.model_code}</td>
                              <td className="px-3 py-2 text-foreground-500">{item.category}</td>
                              <td className="px-3 py-2 text-right text-foreground-400">
                                {item.old_cost_price != null ? formatTRY(item.old_cost_price) : '—'}
                              </td>
                              <td className="px-3 py-2 text-right text-foreground-700">
                                {item.new_cost_price != null ? formatTRY(item.new_cost_price) : '—'}
                              </td>
                            </tr>
                          ))}
                          {batchItems[batch.id].length > 100 && (
                            <tr><td colSpan={5} className="px-3 py-2 text-center text-foreground-400">ve {batchItems[batch.id].length - 100} ürün daha...</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}